import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createMerchantRequestsApi, type MerchantRequestsApi } from "./api.js";
import type {
  AssignmentDraft,
  ConversationMessageDraft,
  CustomerNoteDraft,
  DecisionDraft,
  EscalateOverdueResult,
  GuestRequestCustomerCandidates,
  PriceMatchRequestDetail,
  RequestInternalNoteDraft,
  RequestQueueFilters,
  RequestQueuePage,
} from "./models.js";

export interface MerchantRequestsTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface MerchantRequestsService {
  loadCustomerLinkCandidates(requestId: string): Promise<GuestRequestCustomerCandidates>;
  linkGuestRequestCustomer(requestId: string, customerId: string): Promise<PriceMatchRequestDetail>;
  assignRequest(requestId: string, draft: AssignmentDraft): Promise<PriceMatchRequestDetail>;
  createCustomerNote(requestId: string, draft: CustomerNoteDraft): Promise<PriceMatchRequestDetail>;
  createInternalNote(
    requestId: string,
    draft: RequestInternalNoteDraft,
  ): Promise<PriceMatchRequestDetail>;
  createConversationMessage(
    requestId: string,
    draft: ConversationMessageDraft,
  ): Promise<PriceMatchRequestDetail>;
  uploadAttachment(requestId: string, file: File): Promise<PriceMatchRequestDetail>;
  downloadAttachment(requestId: string, attachmentId: string): Promise<string>;
  removeAttachment(requestId: string, attachmentId: string): Promise<PriceMatchRequestDetail>;
  escalateOverdueRequests(): Promise<EscalateOverdueResult>;
  loadRequest(requestId: string): Promise<PriceMatchRequestDetail>;
  loadRequests(filters: RequestQueueFilters): Promise<RequestQueuePage>;
  retryEvidence(requestId: string): Promise<PriceMatchRequestDetail>;
  resendCustomerEmail(requestId: string): Promise<PriceMatchRequestDetail>;
  retryCouponPreparation(requestId: string): Promise<PriceMatchRequestDetail>;
  selectEvidence(requestId: string, snapshotId: string): Promise<PriceMatchRequestDetail>;
  updateCustomerNote(
    requestId: string,
    noteId: string,
    draft: CustomerNoteDraft,
  ): Promise<PriceMatchRequestDetail>;
  updateInternalNote(
    requestId: string,
    noteId: string,
    draft: RequestInternalNoteDraft,
  ): Promise<PriceMatchRequestDetail>;
  submitDecision(requestId: string, draft: DecisionDraft): Promise<PriceMatchRequestDetail>;
}

export interface MerchantRequestsServiceOptions {
  api?: MerchantRequestsApi;
  apiClient?: MerchantApiClient;
  idempotencyKeyFactory?: () => string;
  telemetry?: MerchantRequestsTelemetry;
}

export function createMerchantRequestsService(
  options: MerchantRequestsServiceOptions,
): MerchantRequestsService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();
  const idempotencyKeyFactory = options.idempotencyKeyFactory ?? (() => crypto.randomUUID());

  return {
    async loadCustomerLinkCandidates(requestId) {
      return api.getCustomerLinkCandidates(requestId);
    },
    async linkGuestRequestCustomer(requestId, customerId) {
      return trackMutation(telemetry, "merchant_guest_request_customer_linked", () =>
        api.linkGuestRequestCustomer(requestId, customerId),
      );
    },
    async assignRequest(requestId, draft) {
      return trackMutation(telemetry, "merchant_request_assigned", () =>
        api.assignRequest(requestId, draft, idempotencyKeyFactory()),
      );
    },
    async createCustomerNote(requestId, draft) {
      return trackMutation(telemetry, "merchant_request_customer_note_created", () =>
        api.createCustomerNote(requestId, draft, idempotencyKeyFactory()),
      );
    },
    async createInternalNote(requestId, draft) {
      return trackMutation(telemetry, "merchant_request_internal_note_created", () =>
        api.createInternalNote(requestId, draft, idempotencyKeyFactory()),
      );
    },
    async createConversationMessage(requestId, draft) {
      return trackMutation(telemetry, "merchant_request_conversation_message_created", () =>
        api.createConversationMessage(requestId, draft, idempotencyKeyFactory()),
      );
    },
    async uploadAttachment(requestId, file) {
      return trackMutation(telemetry, "merchant_request_attachment_uploaded", async () => {
        const upload = await api.createAttachmentUploadUrl(
          requestId,
          file,
          idempotencyKeyFactory(),
        );
        await uploadAttachmentFile(upload.uploadUrl, file, upload.headers);
        return api.completeAttachmentUpload(requestId, upload.attachment.attachmentId, file);
      });
    },
    async downloadAttachment(requestId, attachmentId) {
      return trackMutation(telemetry, "merchant_request_attachment_downloaded", async () => {
        const download = await api.getAttachmentDownloadUrl(requestId, attachmentId);
        return download.downloadUrl;
      });
    },
    async removeAttachment(requestId, attachmentId) {
      return trackMutation(telemetry, "merchant_request_attachment_removed", () =>
        api.removeAttachment(requestId, attachmentId, idempotencyKeyFactory()),
      );
    },
    async escalateOverdueRequests() {
      return trackMutation(telemetry, "merchant_request_overdue_escalated", () =>
        api.escalateOverdueRequests(idempotencyKeyFactory()),
      );
    },
    async loadRequest(requestId) {
      return trackLoad(
        telemetry,
        "merchant_request_detail_loaded",
        () => api.getRequest(requestId),
        { requestId },
      );
    },
    async loadRequests(filters) {
      return trackLoad(telemetry, "request_list_filtered", () => api.listRequests(filters), {
        assignee: filters.assignee,
        outcome: filters.outcome,
        slaState: filters.slaState,
        sort: filters.sort,
        status: filters.status,
      });
    },
    async retryEvidence(requestId) {
      return trackMutation(telemetry, "merchant_request_evidence_retry", () =>
        api.retryEvidence(requestId, idempotencyKeyFactory()),
      );
    },
    async resendCustomerEmail(requestId) {
      return trackMutation(telemetry, "merchant_request_customer_email_resent", () =>
        api.resendCustomerEmail(requestId, idempotencyKeyFactory()),
      );
    },
    async retryCouponPreparation(requestId) {
      return trackMutation(telemetry, "merchant_request_coupon_retry", () =>
        api.retryCouponPreparation(requestId),
      );
    },
    async selectEvidence(requestId, snapshotId) {
      return trackMutation(telemetry, "merchant_request_evidence_selected", () =>
        api.selectEvidence(requestId, snapshotId, idempotencyKeyFactory()),
      );
    },
    async updateCustomerNote(requestId, noteId, draft) {
      return trackMutation(telemetry, "merchant_request_customer_note_updated", () =>
        api.updateCustomerNote(requestId, noteId, draft, idempotencyKeyFactory()),
      );
    },
    async updateInternalNote(requestId, noteId, draft) {
      return trackMutation(telemetry, "merchant_request_internal_note_updated", () =>
        api.updateInternalNote(requestId, noteId, draft, idempotencyKeyFactory()),
      );
    },
    async submitDecision(requestId, draft) {
      return trackMutation(telemetry, "merchant_request_decision_submitted", () =>
        api.submitDecision(requestId, draft, idempotencyKeyFactory()),
      );
    },
  };
}

async function uploadAttachmentFile(
  uploadUrl: string,
  file: File,
  headers: Record<string, string>,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      ...headers,
      "Content-Type": file.type,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error("Attachment upload to storage failed.");
  }
}

async function trackLoad<T>(
  telemetry: MerchantRequestsTelemetry,
  eventName: string,
  operation: () => Promise<T>,
  payload: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    telemetry.track(eventName, {
      ...payload,
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    telemetry.track(eventName, {
      ...payload,
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

async function trackMutation<T>(
  telemetry: MerchantRequestsTelemetry,
  eventName: string,
  operation: () => Promise<T>,
): Promise<T> {
  return trackLoad(telemetry, eventName, operation, {});
}

function createApiFromClient(client: MerchantApiClient | undefined): MerchantRequestsApi {
  if (client === undefined) {
    throw new Error("Merchant requests service requires an API client.");
  }

  return createMerchantRequestsApi(client);
}

function browserTelemetry(): MerchantRequestsTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
